import React, { useEffect, useRef, useState } from 'react';
import { patientsService, Patient } from '../../services/patientsService';
import { FocusedPatient } from '../../contexts/ClinicAgentContext';
import { LockIcon } from '../../assets/icons';

interface PatientFocusPillProps {
  focusedPatient: FocusedPatient | null;
  locked: boolean;
  onToggleLock: () => void;
  onClear: () => void;
  onPick: (patient: FocusedPatient) => void;
}

const patientName = (p: Patient): string =>
  `${p.firstName} ${p.lastName}`.trim() || p.username || `Paciente #${p.id}`;

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

const Avatar: React.FC<{ name: string; url?: string | null; size?: number }> = ({
  name,
  url,
  size = 22,
}) =>
  url ? (
    <img
      src={url}
      alt=""
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
    />
  ) : (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: '#ccfbf1',
        color: '#0f766e',
        fontSize: size * 0.42,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {initials(name)}
    </span>
  );

const PatientFocusPill: React.FC<PatientFocusPillProps> = ({
  focusedPatient,
  locked,
  onToggleLock,
  onClear,
  onPick,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchSeqRef = useRef(0);

  // Close the picker on outside click / Escape.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  // Debounced patient search while the picker is open.
  useEffect(() => {
    if (!pickerOpen) return;
    const seq = ++searchSeqRef.current;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await patientsService.getPatients(1, query);
        if (searchSeqRef.current === seq) setResults(res.results || []);
      } catch (err) {
        console.warn('PatientFocusPill: patient search failed', err);
        if (searchSeqRef.current === seq) setResults([]);
      } finally {
        if (searchSeqRef.current === seq) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, pickerOpen]);

  const openPicker = () => {
    setQuery('');
    setResults([]);
    setPickerOpen(true);
  };

  const pick = (p: Patient) => {
    onPick({ id: p.id, name: patientName(p) });
    setPickerOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {focusedPatient ? (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            maxWidth: '100%',
            padding: '4px 6px 4px 8px',
            borderRadius: 999,
            border: `1px solid ${locked ? '#5eead4' : '#e5e7eb'}`,
            background: locked ? '#f0fdfa' : '#f9fafb',
          }}
        >
          <Avatar name={focusedPatient.name} />
          <button
            onClick={openPicker}
            title="Cambiar paciente"
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              color: '#1f2937',
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {focusedPatient.name}
          </button>
          <button
            onClick={onToggleLock}
            title={locked ? 'Desfijar paciente' : 'Fijar paciente'}
            aria-pressed={locked}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 2,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: locked ? '#0d9488' : '#9ca3af',
            }}
          >
            <LockIcon locked={locked} />
          </button>
          <button
            onClick={onClear}
            aria-label="Quitar paciente"
            title="Quitar paciente"
            style={{
              border: 'none',
              background: 'transparent',
              padding: 2,
              cursor: 'pointer',
              color: '#9ca3af',
              fontSize: 15,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <button
          onClick={openPicker}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            border: '1px dashed #d1d5db',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 13,
            color: '#6b7280',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 14 }}>＋</span>
          Elegir paciente
        </button>
      )}

      {pickerOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 10,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar paciente…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 13,
                outline: 'none',
              }}
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 12, fontSize: 13, color: '#9ca3af' }}>Buscando…</div>
            ) : results.length === 0 ? (
              <div style={{ padding: 12, fontSize: 13, color: '#9ca3af' }}>Sin resultados</div>
            ) : (
              results.map((p) => {
                const name = patientName(p);
                const active = focusedPatient?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => pick(p)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      border: 'none',
                      background: active ? '#f0fdfa' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <Avatar name={name} url={p.avatarUrl} size={26} />
                    <span style={{ overflow: 'hidden' }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#1f2937',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {name}
                      </span>
                      {p.email && (
                        <span
                          style={{
                            display: 'block',
                            fontSize: 11,
                            color: '#9ca3af',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.email}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientFocusPill;
